import {
  Stack,
  Card,
  Group,
  Badge,
  Title,
  Text,
  Button,
  ActionIcon,
  Alert,
  Divider,
  Grid,
  SimpleGrid,
  Loader,
  Center,
} from '@mantine/core';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconArrowLeft,
  IconAlertCircle,
  IconFile,
  IconPencil,
  IconBuilding,
  IconTruck,
  IconUser,
  IconExternalLink,
  IconRoute,
  IconShieldCheck,
  IconCalendar,
  IconHash,
  IconReceipt,
  IconInfoCircle,
  IconNotes,
} from '@tabler/icons-react';
import { useEffect } from 'react';
import { notifications } from '@mantine/notifications';
import {
  useEnvio,
  useEmpresas,
  useVehiculos,
  useConductores,
  useDeCAs,
  getErrorMessage,
} from '../../api/hooks';
import type { Conductor, Empresa, EnvioEstado, Vehiculo } from '../../api/types';

const ESTADO_COLOR: Record<EnvioEstado, string> = {
  BORRADOR: 'gray',
  EMITIDO: 'green',
  EN_CURSO: 'blue',
  FINALIZADO: 'darkgreen',
  CERRADO: 'dark',
  CANCELADO: 'red',
};

const ESTADO_LABEL: Record<EnvioEstado, string> = {
  BORRADOR: 'Borrador',
  EMITIDO: 'Emitido',
  EN_CURSO: 'En curso',
  FINALIZADO: 'Finalizado',
  CERRADO: 'Cerrado',
  CANCELADO: 'Cancelado',
};

const TIPO_SERVICIO_LABEL: Record<string, string> = {
  MERCANCIA: 'Mercancía',
  VIAJERO_DISCRECIONAL: 'Viajeros — Discrecional',
  VIAJERO_TURISTICO: 'Viajeros — Turístico',
  COLABORACION: 'Colaboración entre transportistas',
};

/**
 * Detalle de un envío: cabecera + partes + DeCA asociado.
 *
 * Layout uniforme inspirado en DeCADetail para que ambas vistas (envío
 * y DeCA) tengan el mismo "aire" — Stack maw=1100, cabecera con
 * ActionIcon, sections con titulo + icono + subtitulo + contenido,
 * FieldRow con label a la izquierda y valor a la derecha.
 *
 * La mercancía / los servicios NO se muestran aquí: viven en el DeCA
 * (recurso Servicio) y se ven en detalle completo al abrir el DeCA.
 * Por eso en este detalle solo enlazamos al DeCA si existe.
 */
export function EnvioDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: envio, isLoading, error } = useEnvio(id);
  const { data: empresas = [] } = useEmpresas();
  const { data: vehiculos = [] } = useVehiculos();
  const { data: conductores = [] } = useConductores();
  const { data: decas = [] } = useDeCAs();

  const empresaById = Object.fromEntries(empresas.map((e) => [e.id, e]));
  const vehiculoById = Object.fromEntries(vehiculos.map((v) => [v.id, v]));
  const conductorById = Object.fromEntries(conductores.map((c) => [c.id, c]));

  // DeCAs del envío (el backend lo incluye en envio.decaId, pero también
  // filtramos por envioId para mayor robustez en mocks).
  const decasDelEnvio = decas.filter((d) => d.envioId === id);
  const decaActual = envio?.decaId ? decas.find((d) => d.id === envio.decaId) : decasDelEnvio[0];

  // Mostrar notificación de error solo una vez (al cambiar el id).
  useEffect(() => {
    if (error) {
      notifications.show({
        title: 'No se pudo cargar el envío',
        message: getErrorMessage(error),
        color: 'red',
        icon: <IconAlertCircle size={18} />,
      });
    }
  }, [error]);

  if (isLoading) {
    return (
      <Center mih={200}>
        <Loader />
      </Center>
    );
  }

  if (!envio) {
    return (
      <Stack gap="md" maw={1100} mx="auto" px="md" pt="md">
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={18} />}
            onClick={() => navigate('/envios')}
          >
            Volver
          </Button>
        </Group>
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertCircle size={20} />}
          title="Envío no encontrado"
        >
          No hemos podido recuperar este envío.
        </Alert>
      </Stack>
    );
  }

  const vehiculo = vehiculoById[envio.vehiculoId];
  const cargador = empresaById[envio.cargadorId];
  const transportista = empresaById[envio.transportistaId];
  const conductor = conductorById[envio.conductorId];

  return (
    <Stack gap="md" maw={1100} mx="auto" px="md" pt="md" pb="xl">
      {/* Cabecera */}
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="sm">
          <ActionIcon variant="subtle" onClick={() => navigate('/envios')} aria-label="Volver">
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Title order={2} fw={600}>
            Envío{' '}
            <Text component="span" ff="monospace" inherit>
              {envio.id.slice(0, 8)}
            </Text>
          </Title>
          <Badge size="lg" color={ESTADO_COLOR[envio.estado]} variant="light">
            {ESTADO_LABEL[envio.estado]}
          </Badge>
        </Group>
        <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
          {/* Acciones de ciclo de vida (Iniciar / Finalizar / Cancelar) viven
              en DeCADetail. Aqui solo dejamos las acciones locales del envío. */}
          {envio.estado === 'BORRADOR' && (
            <>
              <Button
                variant="default"
                leftSection={<IconPencil size={16} />}
                onClick={() => navigate(`/envios/${envio.id}/editar`)}
              >
                Editar envío
              </Button>
              <Button
                onClick={() => navigate('/envios', { state: { emitirEnvioId: envio.id } })}
                leftSection={<IconFile size={16} />}
              >
                Emitir DeCA
              </Button>
            </>
          )}
        </Group>
      </Group>

      {/* Cabecera del envío: tipo, estado, fechas */}
      <SectionCard
        title="Cabecera del envío"
        icon={IconReceipt}
        subtitle="Datos del viaje y fechas previstas."
      >
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <FieldRow icon={IconRoute} label="Tipo de servicio">
              {TIPO_SERVICIO_LABEL[envio.tipoServicio] ?? envio.tipoServicio}
            </FieldRow>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <FieldRow icon={IconShieldCheck} label="Estado">
              <Badge variant="light" color={ESTADO_COLOR[envio.estado]}>
                {ESTADO_LABEL[envio.estado]}
              </Badge>
            </FieldRow>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <FieldRow icon={IconCalendar} label="Fecha de servicio">
              {envio.fechaServicio}
            </FieldRow>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <FieldRow icon={IconCalendar} label="Hora salida prevista">
              {envio.horaSalidaPrevista || '—'}
            </FieldRow>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <FieldRow icon={IconCalendar} label="Fecha fin servicio">
              {envio.fechaFinServicio || '—'}
            </FieldRow>
          </Grid.Col>
        </Grid>
      </SectionCard>

      {/* Partes */}
      <SectionCard
        title="Partes"
        icon={IconBuilding}
        subtitle="Cargador contractual, transportista efectivo, conductor y vehículo."
      >
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <EmpresaMiniView titulo="Cargador contractual" icono={IconBuilding} empresa={cargador} />
          <EmpresaMiniView
            titulo="Transportista efectivo"
            icono={IconTruck}
            empresa={transportista}
          />
          <ConductorMiniView conductor={conductor} />
          <VehiculoMiniView vehiculo={vehiculo} />
        </SimpleGrid>
      </SectionCard>

      {/* DeCA asociado */}
      <SectionCard
        title="DeCA asociado"
        icon={IconFile}
        subtitle={
          decaActual
            ? 'Documento electrónico de control administrativo generado a partir de este envío.'
            : 'Aún no se ha emitido un DeCA para este envío.'
        }
      >
        {decaActual ? (
          <Stack gap="md">
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <FieldRow icon={IconHash} label="Código anual" alignTop>
                  <Text ff="monospace" fw={600}>
                    {decaActual.codigoAnual ?? '—'}
                  </Text>
                  <Text size="xs" c="dimmed" ff="monospace">
                    público {decaActual.codigoPublico}
                  </Text>
                </FieldRow>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <FieldRow icon={IconShieldCheck} label="Estado legal">
                  <Badge
                    variant="light"
                    color={
                      decaActual.estado === 'VIGENTE' || decaActual.estado === 'EN_CURSO'
                        ? 'deca'
                        : 'gray'
                    }
                  >
                    {decaActual.estado}
                  </Badge>
                </FieldRow>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <FieldRow icon={IconCalendar} label="Fecha de emisión">
                  {new Date(decaActual.fechaEmision).toLocaleString('es-ES')}
                </FieldRow>
              </Grid.Col>
            </Grid>
            <Group>
              <Button
                variant="light"
                color="deca"
                leftSection={<IconExternalLink size={16} />}
                onClick={() => navigate(`/deca/${decaActual.id}`)}
              >
                Ver detalle del DeCA
              </Button>
            </Group>
            <Alert
              color="deca"
              variant="light"
              icon={<IconRoute size={18} />}
              title="Mercancía y servicios"
            >
              Los datos de origen, destino y mercancía viven en los servicios del DeCA. Ábrelo para
              ver el detalle completo (origen → destino, peso, bultos, naturaleza).
            </Alert>
          </Stack>
        ) : (
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              {envio.estado === 'BORRADOR'
                ? 'Pulsa "Emitir DeCA" arriba para generarlo.'
                : 'Este envío no tiene un DeCA asociado.'}
            </Text>
            {envio.estado === 'BORRADOR' && (
              <div>
                <Button
                  leftSection={<IconFile size={16} />}
                  onClick={() => navigate('/envios', { state: { emitirEnvioId: envio.id } })}
                >
                  Emitir DeCA
                </Button>
              </div>
            )}
          </Stack>
        )}
      </SectionCard>

      {/* Observaciones */}
      {envio.observaciones && (
        <SectionCard
          title="Observaciones"
          icon={IconNotes}
          subtitle="Notas internas sobre el envío."
        >
          <Text c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
            {envio.observaciones}
          </Text>
        </SectionCard>
      )}

      {/* Auditoría */}
      <SectionCard
        title="Auditoría"
        icon={IconInfoCircle}
        subtitle="Metadatos de creación y última modificación."
      >
        <Grid>
          {envio.createdAt && (
            <Grid.Col span={{ base: 12, md: 6 }}>
              <FieldRow label="Fecha de creación">
                {new Date(envio.createdAt).toLocaleString('es-ES')}
              </FieldRow>
            </Grid.Col>
          )}
          {envio.updatedAt && (
            <Grid.Col span={{ base: 12, md: 6 }}>
              <FieldRow label="Última actualización">
                {new Date(envio.updatedAt).toLocaleString('es-ES')}
              </FieldRow>
            </Grid.Col>
          )}
        </Grid>
      </SectionCard>
    </Stack>
  );
}

// ────────────────────────────────────────────────────────────────────
// Subcomponentes de layout uniforme (mismo patrón que DeCADetail)
// ────────────────────────────────────────────────────────────────────

interface SectionCardProps {
  title: string;
  icon: typeof IconReceipt;
  subtitle?: string;
  children: React.ReactNode;
}
function SectionCard({ title, icon: IconCmp, subtitle, children }: SectionCardProps) {
  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="md">
        <Group gap="xs" align="center">
          <IconCmp size={20} color="var(--mantine-color-deca-7)" />
          <Title order={4} fw={600}>
            {title}
          </Title>
        </Group>
        {subtitle && (
          <Text size="sm" c="dimmed">
            {subtitle}
          </Text>
        )}
        <Divider />
        {children}
      </Stack>
    </Card>
  );
}

interface FieldRowProps {
  icon?: typeof IconReceipt;
  label: string;
  alignTop?: boolean;
  children: React.ReactNode;
}
function FieldRow({ icon: IconCmp, label, alignTop, children }: FieldRowProps) {
  return (
    <Group justify="space-between" align={alignTop ? 'flex-start' : 'center'} wrap="nowrap">
      <Group gap={6} wrap="nowrap" style={{ minWidth: 0, flex: '0 0 auto' }}>
        {IconCmp && <IconCmp size={14} color="var(--mantine-color-gray-6)" />}
        <Text size="sm" c="dimmed">
          {label}
        </Text>
      </Group>
      <Stack gap={2} align="flex-end" style={{ minWidth: 0, flex: 1 }}>
        {children}
      </Stack>
    </Group>
  );
}

interface EmpresaMiniViewProps {
  titulo: string;
  icono: typeof IconBuilding;
  empresa: Empresa | undefined;
}
function EmpresaMiniView({ titulo, icono: IconCmp, empresa }: EmpresaMiniViewProps) {
  return (
    <Card withBorder padding="sm" radius="sm">
      <Stack gap={6}>
        <Group gap={6}>
          <IconCmp size={14} color="var(--mantine-color-deca-7)" />
          <Text size="sm" fw={600}>
            {titulo}
          </Text>
        </Group>
        {empresa ? (
          <>
            <Text size="sm" fw={500}>
              {empresa.razonSocial}
            </Text>
            {empresa.nombreComercial && (
              <Text size="xs" c="dimmed">
                {empresa.nombreComercial}
              </Text>
            )}
            <Group gap={4}>
              <Text size="xs" c="dimmed">
                NIF/CIF:
              </Text>
              <Text size="xs" ff="monospace">
                {empresa.nifCif}
              </Text>
            </Group>
            <Text size="xs" c="dimmed">
              {empresa.domicilioFiscal}
            </Text>
            {(empresa.municipio || empresa.provincia) && (
              <Text size="xs" c="dimmed">
                {[empresa.codigoPostal, empresa.municipio, empresa.provincia]
                  .filter(Boolean)
                  .join(', ')}
              </Text>
            )}
          </>
        ) : (
          <Text size="sm" c="dimmed">
            No asignada
          </Text>
        )}
      </Stack>
    </Card>
  );
}

function ConductorMiniView({ conductor }: { conductor: Conductor | undefined }) {
  return (
    <Card withBorder padding="sm" radius="sm">
      <Stack gap={6}>
        <Group gap={6}>
          <IconUser size={14} color="var(--mantine-color-deca-7)" />
          <Text size="sm" fw={600}>
            Conductor
          </Text>
        </Group>
        {conductor ? (
          <>
            <Text size="sm" fw={500}>
              {conductor.nombre}
            </Text>
            {conductor.dni && (
              <Group gap={4}>
                <Text size="xs" c="dimmed">
                  DNI:
                </Text>
                <Text size="xs" ff="monospace">
                  {conductor.dni}
                </Text>
              </Group>
            )}
            {conductor.telefono && (
              <Text size="xs" c="dimmed">
                {conductor.telefono}
              </Text>
            )}
          </>
        ) : (
          <Text size="sm" c="dimmed">
            No asignado
          </Text>
        )}
      </Stack>
    </Card>
  );
}

function VehiculoMiniView({ vehiculo }: { vehiculo: Vehiculo | undefined }) {
  return (
    <Card withBorder padding="sm" radius="sm">
      <Stack gap={6}>
        <Group gap={6}>
          <IconTruck size={14} color="var(--mantine-color-deca-7)" />
          <Text size="sm" fw={600}>
            Vehículo
          </Text>
        </Group>
        {vehiculo ? (
          <>
            <Text size="sm" fw={500} ff="monospace">
              {vehiculo.matriculaRemolque
                ? `${vehiculo.matricula} / ${vehiculo.matriculaRemolque}`
                : vehiculo.matricula}
            </Text>
            {(vehiculo.marca || vehiculo.modelo) && (
              <Text size="xs" c="dimmed">
                {[vehiculo.marca, vehiculo.modelo].filter(Boolean).join(' ')}
              </Text>
            )}
          </>
        ) : (
          <Text size="sm" c="dimmed">
            No asignado
          </Text>
        )}
      </Stack>
    </Card>
  );
}
